import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { Icon, type IconName, figmaAssets } from './icons';

export const cx = (...classes: Array<string | undefined | null | false>) => classes.filter(Boolean).join(' ');

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'icon';
  loading?: boolean;
  iconOnly?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', type = 'button', disabled, loading = false, iconOnly = false, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('sv-button', `sv-button--${variant}`, `sv-button--${size}`, iconOnly && 'sv-button--icon-only', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <span className="sv-spinner" aria-hidden="true" />}
      {children}
    </button>
  );
});

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'green' | 'amber' | 'red' | 'blue' | 'violet';
  dot?: boolean;
}

const badgeDots = {
  neutral: 'kit-dot',
  green: 'page-dot',
  amber: 'page-dot1',
  red: 'kit-dot3',
  blue: 'kit-dot4',
  violet: 'kit-dot5',
} as const;

export function Badge({ tone = 'neutral', dot = false, className, children, ...props }: BadgeProps) {
  return <span className={cx('sv-badge', `sv-badge--${tone}`, className)} {...props}>
    {dot && <Icon name={badgeDots[tone]} />}
    {children}
  </span>;
}

export type CheckboxProps = CheckboxPrimitive.CheckboxProps;

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { className, checked, defaultChecked, ...props }, ref,
) {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cx('sv-checkbox', className)}
      checked={checked}
      defaultChecked={defaultChecked}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="sv-checkbox__indicator">
        <Icon name="page-check" className="sv-checkbox__check" />
        <span className="sv-checkbox__dash" aria-hidden="true" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cx('sv-input', className)} {...props} />;
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  wrapperClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, wrapperClassName, children, ...props }, ref,
) {
  return (
    <span className={cx('sv-select-wrap', wrapperClassName)}>
      <select ref={ref} className={cx('sv-select', className)} {...props}>{children}</select>
      <Icon name="page-chevron-down" className="sv-select__chevron" />
    </span>
  );
});

export function Counter({ className, children, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cx('sv-counter', className)} {...props}>{children}</span>;
}

export function Kbd({ className, children, ...props }: HTMLAttributes<HTMLElement>) {
  return <kbd className={cx('sv-kbd', className)} {...props}>{children}</kbd>;
}

export interface RecordTypeBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  type?: 'БЗ' | 'ЕХ' | 'АЗ';
}

export function RecordTypeBadge({ type = 'БЗ', className, ...props }: RecordTypeBadgeProps) {
  const tone = type === 'БЗ' ? 'bib' : type === 'ЕХ' ? 'holding' : 'authority';
  return <span className={cx('sv-record-type', `sv-record-type--${tone}`, className)} {...props}>{type}</span>;
}

export type SwitchProps = SwitchPrimitive.SwitchProps;

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { className, ...props }, ref,
) {
  return <SwitchPrimitive.Root ref={ref} className={cx('sv-switch', className)} {...props}>
    <img src={figmaAssets['kit-state-off'].src} width={figmaAssets['kit-state-off'].width} height={figmaAssets['kit-state-off'].height} alt="" aria-hidden="true" className="sv-switch__off" />
    <img src={figmaAssets['kit-state-on'].src} width={figmaAssets['kit-state-on'].width} height={figmaAssets['kit-state-on'].height} alt="" aria-hidden="true" className="sv-switch__on" />
  </SwitchPrimitive.Root>;
});

export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  value: string;
  active?: boolean;
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  { label, value, active = false, className, type = 'button', ...props }, ref,
) {
  return <button ref={ref} type={type} className={cx('sv-filter-chip', active && 'sv-filter-chip--active', className)} {...props}>
    <span className="sv-filter-chip__label">{label}</span>
    <span className="sv-filter-chip__value">{value}</span>
    <Icon name={active ? 'kit-chevron1' : 'page-filter-chevron'} />
  </button>;
});

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: IconName;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, icon = 'search', action, className }: EmptyStateProps) {
  return <div className={cx('sv-empty-state', className)} role="status">
    <div className="sv-empty-state__icon"><Icon name={icon} /></div>
    <h3 className="sv-empty-state__title">{title}</h3>
    {description && <p className="sv-empty-state__description">{description}</p>}
    {action && <div className="sv-empty-state__action">{action}</div>}
  </div>;
}

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, hint, error, className, children, ...props }: FieldProps) {
  return <div className={cx('sv-field', className)} {...props}>
    <label className="sv-field__label" htmlFor={htmlFor}>{label}</label>
    {children}
    {(error || hint) && <p className={cx('sv-field__hint', !!error && 'sv-field__error')} role={error ? 'alert' : undefined}>{error || hint}</p>}
  </div>;
}
